import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  ViewEncapsulation,
  booleanAttribute,
  signal,
  computed,
  Input,
} from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ShadowDomStyleInjectorService, SuggestionOption } from '@everymatrix-base-components/utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import { AutoCompleteModule } from 'primeng/autocomplete';

interface AutoCompleteCompleteEvent {
    originalEvent: Event;
    query: string;
}

@Component({
  selector: 'lib-dm-input-text-lib',
  standalone: true,
  imports: [
    InputTextModule,
    ReactiveFormsModule,
    CommonModule,
    AutoCompleteModule,
  ],
  template: `
    <div
      class="form-control-container"
      [ngStyle]="{ visibility: !attachedToShadowDom() ? 'hidden' : 'visible' }"
    >
      @if(label()) {
      <label [attr.for]="'inputField-' + uniqueId()" class="form-control-label">
        {{ computedLabel() }}
      </label>
      }
      <div [ngStyle]="{ position: 'relative' }" class="input-text-container" [ngClass]="{'has-value-on-hover': showClear() && currentControl()?.value && isHovered }">
        @if(autocomplete) {
          <p-autocomplete
            #inputElement
            [ngStyle]="{ display: 'block', padding: 0, border: 'none' }"
            (input)="onInputChange($event)"
            (click)="clickEmitter.emit()"
            pInputText
            [id]="'inputField-' + uniqueId()"
            [readonly]="readonly()"
            [formControl]="currentControl()"
            [placeholder]="'Enter ' + placeholder()"
            (blur)="markAsTouched(); isFocused = false"
            (focus)="isFocused = true"
            optionLabel="name"
            [suggestions]="filteredSuggestions"
            [showEmptyMessage]="showEmptyMessage"
            (completeMethod)="onComplete($event)"
            (onSelect)="onSuggestionSelect($event)"
            fluid
            [appendTo]="appendToBody ? 'body' : null"
            (mouseenter)="isHovered = true"
            (mouseleave)="isHovered = false"
          />
        } @else {
          <input
            #inputElement
            (input)="onInputChange($event)"
            (click)="clickEmitter.emit()"
            autocomplete="off"
            class="form-control-field"
            pInputText
            [id]="'inputField-' + uniqueId()"
            [readonly]="readonly()"
            [formControl]="currentControl()"
            [placeholder]="'Enter ' + placeholder()"
            (blur)="markAsTouched(); isFocused = false"
            (focus)="isFocused = true"
            [attr.part]="inputPartName"
            (mouseenter)="isHovered = true"
            (mouseleave)="isHovered = false"
          />
        } 
        @if(showClear() && currentControl()?.value) {
        <svg
          (click)="onClear()"
          class="clear-icon"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M8.01186 7.00933L12.27 2.75116C12.341 2.68501 12.398 2.60524 12.4375 2.51661C12.4769 2.42798 12.4982 2.3323 12.4999 2.23529C12.5016 2.13827 12.4838 2.0419 12.4474 1.95194C12.4111 1.86197 12.357 1.78024 12.2884 1.71163C12.2198 1.64302 12.138 1.58893 12.0481 1.55259C11.9581 1.51625 11.8617 1.4984 11.7647 1.50011C11.6677 1.50182 11.572 1.52306 11.4834 1.56255C11.3948 1.60204 11.315 1.65898 11.2488 1.72997L6.99067 5.98814L2.7325 1.72997C2.59553 1.60234 2.41437 1.53286 2.22718 1.53616C2.03999 1.53946 1.8614 1.61529 1.72901 1.74767C1.59663 1.88006 1.5208 2.05865 1.5175 2.24584C1.5142 2.43303 1.58368 2.61419 1.71131 2.75116L5.96948 7.00933L1.71131 11.2675C1.576 11.403 1.5 11.5866 1.5 11.7781C1.5 11.9696 1.576 12.1532 1.71131 12.2887C1.84679 12.424 2.03043 12.5 2.2219 12.5C2.41338 12.5 2.59702 12.424 2.7325 12.2887L6.99067 8.03052L11.2488 12.2887C11.3843 12.424 11.568 12.5 11.7594 12.5C11.9509 12.5 12.1346 12.424 12.27 12.2887C12.4053 12.1532 12.4813 11.9696 12.4813 11.7781C12.4813 11.5866 12.4053 11.403 12.27 11.2675L8.01186 7.00933Z"
            fill="#5D6983"
          ></path>
        </svg>
        }
        <ng-content select="[icon-left]"></ng-content>
        <ng-content select="[icon-right]"></ng-content>
      </div>
      @if(showError() && !isFocused) {
      <div>
        <p class="form-control-error-message">{{ computedErrorMessage() }}</p>
      </div>
      } @if(hint() && isFocused && !permanentHint()) {
      <div>
        <p class="form-control-hint-message">{{ hint() }}</p>
      </div>
      } @if(hint() && permanentHint() && !showError()) {
      <div>
        <p class="form-control-hint-message">{{ hint() }}</p>
      </div>
      }
    </div>
  `,
  styleUrls: ['./dm-input-text-lib.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class DmInputTextLibComponent implements AfterViewInit {
  private elementRef = inject(ElementRef);
  private shadowDomStyleInjectorService = inject(ShadowDomStyleInjectorService);

  label = input<string>('');
  hint = input<string>('');
  permanentHint = input<boolean>(false);
  showClear = input(true, { transform: booleanAttribute });
  placeholder = input<string>('');
  readonly = input(false, { transform: booleanAttribute });
  errorMessage = input<string>('Invalid input.');
  control = input<any>(null);
  attachedToShadowDom = signal(false);
  isFocused = false;
  isHovered = false;

  clickEmitter = output<void>();

  @Input()
  suggestionsList: SuggestionOption[] = [];
  filteredSuggestions: SuggestionOption[] = [];
  @Input()
  autocomplete!: boolean;
  @Input()
  searchCharacter!: string | undefined;
  @Input()
  showEmptyMessage!: boolean;
  @Input({ transform: booleanAttribute }) appendToBody = false;

  @Input()
  inputPartName!: string;

  protected uniqueId = signal(crypto.randomUUID());
  protected internalControl = new FormControl<string | null>('');

  protected currentControl = computed(
    () => this.control() ?? this.internalControl
  );
  keyUpValue: string = this.getCurrentValue();
  keyUpLastChar: string | null = this.getCurrentValue()[this.getCurrentValue().length - 1];

  private trigger = toSignal(
    merge(
      this.currentControl().statusChanges,
      this.currentControl().valueChanges
    ),
    { initialValue: null }
  );
  private manualTrigger = signal(0);

  computedLabel = computed(() => {
    const baseLabel = this.label();
    const isRequired = this.currentControl().hasValidator(Validators.required);

    return isRequired ? `${baseLabel} *` : baseLabel;
  });

  computedErrorMessage = computed(() => {
    this.trigger();
    this.manualTrigger();

    const ctrl = this.currentControl();

    if (!ctrl.invalid || !(ctrl.dirty || ctrl.touched)) {
      return '';
    }

    if (ctrl.hasError('required')) {
      return 'This field is required.';
    }

    if (ctrl.hasError('pattern')) {
      return (
        this.errorMessage() || 'Input does not match the required pattern.'
      );
    }

    if (ctrl.hasError('email')) {
      return 'Email address is not valid';
    }

    if (ctrl.hasError('minlength')) {
      return `Minimum length is ${ctrl.getError('minlength').requiredLength}.`;
    }

    if (ctrl.hasError('maxlength')) {
      return `Maximum length is ${ctrl.getError('maxlength').requiredLength}.`;
    }

    return '';
  });

  showError = computed(() => {
    this.trigger();
    this.manualTrigger();
    const ctrl = this.currentControl();

    return ctrl.invalid && (ctrl.dirty || ctrl.touched);
  });

  ngAfterViewInit(): void {
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.elementRef)
      .subscribe((data) => this.attachedToShadowDom.set(data));
  }

  markAsTouched(): void {
    const ctrl = this.currentControl();
    ctrl.markAsTouched();
    this.manualTrigger.update((v) => v + 1); // Force update
  }

  onInputChange(event: any): void {
    const value = (event.target as HTMLInputElement).value;
    const ctrl = this.currentControl();
    ctrl.setValue(value);
    this.elementRef.nativeElement.dispatchEvent(
      new CustomEvent('dm-input-text-value-change', {
        detail: value,
        bubbles: true,
        composed: true,
      })
    );

    if (this.autocomplete) {
      this.keyUpValue = value;
      this.keyUpLastChar = event?.data;
    }
  }

  onClear() {
    this.currentControl().reset();
    this.manualTrigger.set(0);
  }

  onComplete(event: AutoCompleteCompleteEvent): void {
    if (this.autocomplete) {
      const value: string = event.query;
      if (
        !value ||
        (this.searchCharacter && !value.includes(this.searchCharacter))
      ) {
        this.filteredSuggestions = [];
        return;
      }

      if (this.searchCharacter) {
        if (this.keyUpLastChar == this.searchCharacter) {
          this.filteredSuggestions = this.suggestionsList.slice();
        } else {
          this.filteredSuggestions = [];
        }
      } else {
        this.filteredSuggestions = this.suggestionsList.filter((s) =>
          s.name.toLowerCase().includes(value)
        );
      }
    }
  }

  onSuggestionSelect(event: { value: SuggestionOption }): void {
    const selectedValue = event.value;
    const ctrl = this.currentControl();
    ctrl.setValue(
      this.checkForCustomSymbol(
        this.keyUpValue,
        selectedValue.id || selectedValue.name
      )
    );
  }

  private getCurrentValue(): string {
    return this.currentControl()?.value || '';
  }

  private checkForCustomSymbol(str: string, input: string): string {
    if (
      this.searchCharacter &&
      str &&
      str.split(this.searchCharacter).length > 1
    ) {
      const transformedArr = str.split(this.searchCharacter);
      transformedArr.splice(-1, 1, input);
      return transformedArr.join(this.searchCharacter);
    }
    return input || '';
  }
}
